import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  Edit, 
  Trash2,
  Loader2,
  Clock,
  User,
  Building,
  Calendar,
  Sparkles,
  Map,
  Play,
  Save,
  CheckCircle,
  RefreshCw,
  X,
  ZoomIn,
  Zap,
  Camera,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const ProcessCaptureDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user, isAdmin } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [capture, setCapture] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [converting, setConverting] = useState(false);
  const [generatingSop, setGeneratingSop] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);

  useEffect(() => {
    fetchCapture();
  }, [id]);

  useEffect(() => {
    // Check if we should show convert dialog
    if (searchParams.get('convert') === 'true' && capture?.status === 'Ready') {
      setShowConvertDialog(true);
    }
  }, [searchParams, capture]);

  const fetchCapture = async () => {
    try {
      const response = await api.get(`/process-captures/${id}`);
      setCapture(response.data);
    } catch (error) {
      console.error('Failed to fetch capture:', error);
      toast.error('Failed to load process capture');
      navigate('/process-capture');
    } finally {
      setLoading(false);
    }
  };

  const handleEnhanceWithAI = async () => {
    setEnhancing(true);
    try {
      const response = await api.post(`/process-captures/${id}/ai-enhance`);
      toast.success('Steps enhanced with AI!');
      fetchCapture();
    } catch (error) {
      console.error('Failed to enhance:', error);
      toast.error('Failed to enhance steps');
    } finally {
      setEnhancing(false);
    }
  };

  const handleConvertToProcessMap = async () => {
    setConverting(true);
    try {
      const response = await api.post(`/process-captures/${id}/convert-to-process-map`);
      toast.success('Process map created!');
      setShowConvertDialog(false);
      navigate(`/process-maps/${response.data.process_map_id}`);
    } catch (error) {
      toast.error('Failed to create process map');
    } finally {
      setConverting(false);
    }
  };

  const handleGenerateSop = async () => {
    setGeneratingSop(true);
    try {
      const response = await api.post('/sops/generate', { capture_id: id, name: `SOP - ${capture?.name || 'Process'}` });
      toast.success('SOP generated!');
      navigate(`/sop-generator/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to generate SOP');
    } finally {
      setGeneratingSop(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this capture?')) return;
    
    try {
      await api.delete(`/process-captures/${id}`);
      toast.success('Process capture deleted');
      navigate('/process-capture');
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete capture');
    }
  };

  const handleUpdateStep = async (stepId, newDescription) => {
    try {
      const updatedSteps = capture.steps.map(s => 
        s.id === stepId ? { ...s, description: newDescription } : s
      );
      await api.put(`/process-captures/${id}`, { steps: updatedSteps });
      setCapture(prev => ({ ...prev, steps: updatedSteps }));
      setEditingStep(null);
      toast.success('Step updated');
    } catch (error) {
      toast.error('Failed to update step');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ready': return 'bg-emerald-100 text-emerald-700';
      case 'Draft': return 'bg-slate-100 text-slate-700';
      case 'Processing': return 'bg-blue-100 text-blue-700';
      case 'Archived': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const canEdit = () => {
    return isAdmin() || capture?.created_by === user?.id;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  if (!capture) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Process capture not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="capture-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                {capture.name}
              </h1>
              <Badge className={getStatusColor(capture.status)}>
                {capture.status}
              </Badge>
            </div>
            {capture.description && (
              <p className="text-slate-500 mt-1">{capture.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {capture.status !== 'Ready' && capture.steps?.length > 0 && (
            <Button 
              variant="outline"
              onClick={handleEnhanceWithAI}
              disabled={enhancing}
              className="border-[#26B8A7] text-[#26B8A7] hover:bg-[#26B8A7]/10"
            >
              {enhancing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              AI Enhance Steps
            </Button>
          )}
          {capture.status === 'Ready' && (
            <>
            <Button 
              onClick={() => setShowConvertDialog(true)}
              className="bg-gradient-to-r from-[#235894] to-[#26B8A7]"
            >
              <Map className="w-4 h-4 mr-2" />
              Convert to Process Map
            </Button>
            <Button
              onClick={handleGenerateSop}
              disabled={generatingSop}
              variant="outline"
              className="border-[#235894] text-[#235894]"
            >
              {generatingSop ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BookOpen className="w-4 h-4 mr-2" />}
              Generate SOP
            </Button>
            </>
          )}
          {canEdit() && (
            <Button 
              variant="outline" 
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Info Panel */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">
              Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Duration</p>
                <p className="font-medium">{formatDuration(capture.duration)}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Building className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Department</p>
                <p className="font-medium">{capture.department || '-'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Created By</p>
                <p className="font-medium">{capture.created_by_name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Created</p>
                <p className="font-medium">
                  {new Date(capture.created_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>

            {capture.linked_process_maps?.length > 0 && (
              <div className="pt-4 border-t">
                <p className="text-xs text-slate-500 mb-2">Linked Process Maps</p>
                {capture.linked_process_maps.map((mapId) => (
                  <Button
                    key={mapId}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start mb-1"
                    onClick={() => navigate(`/process-maps/${mapId}`)}
                  >
                    <Map className="w-4 h-4 mr-2" />
                    View Process Map
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Steps */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recorded Steps</CardTitle>
                <CardDescription>
                  {capture.steps?.length || 0} steps captured
                </CardDescription>
              </div>
              {capture.steps?.length > 0 && capture.status !== 'Ready' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEnhanceWithAI}
                  disabled={enhancing}
                >
                  {enhancing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Enhance All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {capture.steps?.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <p>No steps recorded yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {capture.steps.map((step, index) => (
                  <div 
                    key={step.id}
                    className="rounded-lg border bg-white overflow-hidden"
                  >
                    {/* Screenshot */}
                    {step.screenshot_url && (
                      <div 
                        className="relative bg-slate-900 cursor-pointer group"
                        onClick={() => setExpandedImage(step.screenshot_url)}
                      >
                        <img
                          src={step.screenshot_url}
                          alt={`Step ${index + 1} screenshot`}
                          className="w-full max-h-64 object-contain"
                          data-testid={`step-screenshot-${index}`}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    )}
                    {/* Step info */}
                    <div className="flex gap-4 p-4">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-[#235894] text-white flex items-center justify-center font-medium text-sm">
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">
                            {formatDuration(step.timestamp)}
                          </Badge>
                          {step.action_type === 'auto' ? (
                            <Badge className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-100">
                              <Zap className="w-3 h-3 mr-0.5" /> Auto
                            </Badge>
                          ) : (
                            <Badge className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-100">
                              <Camera className="w-3 h-3 mr-0.5" /> Manual
                            </Badge>
                          )}
                          {!step.screenshot_url && (
                            <Badge variant="outline" className="text-xs text-slate-400">
                              No screenshot
                            </Badge>
                          )}
                        </div>
                        {editingStep === step.id ? (
                          <div className="flex gap-2 mt-2">
                            <Input
                              defaultValue={step.description}
                              id={`step-input-${step.id}`}
                              placeholder="Describe this step..."
                              className="flex-1"
                            />
                            <Button
                              size="sm"
                              onClick={() => {
                                const input = document.getElementById(`step-input-${step.id}`);
                                handleUpdateStep(step.id, input.value);
                              }}
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-slate-700">
                            {step.description || step.element_text || 'No description'}
                          </p>
                        )}
                      </div>
                      {canEdit() && editingStep !== step.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingStep(step.id)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Convert Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Map className="w-5 h-5 text-[#235894]" />
              Convert to Process Map
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              AI will analyze your recorded steps and generate a BPMN process map automatically.
            </p>
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
              <p className="font-medium">This will:</p>
              <ul className="mt-1 space-y-1">
                <li>- Create a new process map with BPMN diagram</li>
                <li>- Link it to this capture</li>
                <li>- Group related steps into tasks</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvertToProcessMap}
              disabled={converting}
              className="bg-[#235894] hover:bg-[#1E4B80]"
            >
              {converting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Convert with AI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Screenshot Lightbox */}
      {expandedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setExpandedImage(null)}
          data-testid="screenshot-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-slate-300 transition-colors"
            onClick={() => setExpandedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={expandedImage}
            alt="Screenshot full view"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ProcessCaptureDetail;
